import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { auth } from '../firebase';
import { importPOSTips, ImportResult, getPendingImportSummary, deleteBatchTransactions, getImportHistory } from '../services/posImportService';
import { ImportHistory } from '../types';
import { Upload, FileType, CheckCircle, AlertTriangle, Download, Info, Settings2, Save, UserPlus, ArrowRight, X, Trash2, History, Zap, MapPin, Building2 } from 'lucide-react';
import Papa from 'papaparse';
import { getLocalDate } from '../lib/utils';
import { formatCurrency } from '../lib/formatters';
import { getSettings, saveSettings, TenantSettings } from '../services/settingsService';
import { StaffManagementForm } from './StaffManagementForm';
import { useStoreLocation } from '../contexts/LocationContext';

interface Props {
  tenantId: string;
}

export const POSImportForm: React.FC<Props> = ({ tenantId }) => {
  const { activeLocation, activeLocationId, isConsolidated, availableLocations } = useStoreLocation();
  const [file, setFile] = useState<File | null>(null);
  const [status, setStatus] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [showMapping, setShowMapping] = useState(false);
  const [settings, setSettings] = useState<TenantSettings | null>(null);
  const [mapping, setMapping] = useState<TenantSettings['posMapping']>({});
  const [isSavingMapping, setIsSavingMapping] = useState(false);
  const [activeView, setActiveView] = useState<'import' | 'history' | 'config'>('import');
  
  // Multi-location: determine effective target store for the import
  const nonConsolidatedLocations = availableLocations.filter(l => !l.isConsolidatedOption);
  const [selectedTargetLocationId, setSelectedTargetLocationId] = useState<string>(() => {
    if (activeLocationId && activeLocationId !== 'all' && activeLocationId !== 'employee_personal') {
      return activeLocationId;
    }
    return nonConsolidatedLocations[0]?.id || 'loc-downtown';
  });

  // Keep selected target store in sync if user changes location in header
  useEffect(() => {
    if (activeLocationId && activeLocationId !== 'all' && activeLocationId !== 'employee_personal') {
      setSelectedTargetLocationId(activeLocationId);
    }
  }, [activeLocationId]);
  
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [showAddStaff, setShowAddStaff] = useState<{ posId: string; locationId?: string; locationName?: string } | null>(null); // Orphan identity mapping
  const [pendingImports, setPendingImports] = useState<{ date: string, count: number, total: number, ids: string[] }[]>([]);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ids: string[], date: string} | null>(null);
  const [isOverride, setIsOverride] = useState(false);
  const [isCorrection, setIsCorrection] = useState(false);
  const [history, setHistory] = useState<ImportHistory[]>([]);
  const [targetBatchId, setTargetBatchId] = useState('');

  const loadPending = async () => {
    const summary = await getPendingImportSummary(tenantId);
    setPendingImports(summary);
  };

  useEffect(() => {
    const loadSettings = async () => {
      const s = await getSettings(tenantId);
      setSettings(s || { tenantId, allocationMethod: 'Percentage', payoutMethod: 'TipsCheck', payCycle: 'Weekly', payDay: 'Friday', payDayDue: 'Thursday', posTipHandling: 'Reported', payrollProvider: 'ADP', displayTipBreakdown: true });
      if (s) {
        setMapping(s.posMapping || {});
      }
    };
    loadSettings();
    loadPending();
    const loadHistory = async () => {
        const h = await getImportHistory(tenantId);
        setHistory(h);
    }
    loadHistory();
  }, [tenantId]);

  const handleDeleteImports = async (ids: string[], date: string) => {
    setConfirmDelete({ ids, date });
  };

  const executeDelete = async () => {
    if (!confirmDelete) return;
    const { ids, date } = confirmDelete;
    setConfirmDelete(null);
    setIsDeleting(date);
    try {
      await deleteBatchTransactions(tenantId, ids);
      await loadPending();
      setStatus(`Successfully cleared pending imports for ${date}.`);
    } catch (e) {
      console.error(e);
      setStatus('Failed to delete pending imports.');
    } finally {
      setIsDeleting(null);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
      setStatus('');
      setImportResult(null);
    }
  };

  const handleTabChange = (view: 'import' | 'history' | 'config') => {
    setActiveView(view);
    setStatus('');
    setImportResult(null);
  };

  const handleSaveMapping = async () => {
    if (!mapping?.posId || !mapping?.amount || !mapping?.tipDate) {
      setStatus('Error: Please map POS ID, Amount, and Tip Date columns.');
      return;
    }
    setIsSavingMapping(true);
    try {
      const currentSettings = settings || { tenantId, allocationMethod: 'Percentage', payoutMethod: 'TipsCheck', payCycle: 'Weekly', payDay: 'Friday', payDayDue: 'Thursday', posTipHandling: 'Reported', payrollProvider: 'ADP', displayTipBreakdown: true };
      const updatedSettings = { ...currentSettings, posMapping: mapping };
      await saveSettings(tenantId, updatedSettings);
      setSettings(updatedSettings);
      setStatus('Mapping saved successfully!');
      setTimeout(() => setStatus(''), 3000);
      setShowMapping(false);
    } catch (error) {
      setStatus('Error saving mapping');
    } finally {
      setIsSavingMapping(false);
    }
  };

  const downloadTemplate = async () => {
    const settings = await getSettings(tenantId);
    const timezone = settings?.timezone || 'UTC';
    const today = getLocalDate(timezone);
    
    const csv = Papa.unparse([
      {
        "Staff Name": "John Doe",
        "Email": "john@example.com",
        "Tip Date": today,
        "Amount": "45.50",
        "POS_ID": "EXT-101",
        "Shift Name": "Morning",
        "Shift Start": "08:00",
        "Shift End": "14:00",
        "Service Charge": "25.00",
        "Transaction ID": "TX-12345",
        "Transaction Timestamp": "2026-06-20 10:00:00"
      },
      {
        "Staff Name": "Jane Smith",
        "Email": "jane@example.com",
        "Tip Date": today,
        "Amount": "62.00",
        "POS_ID": "EXT-102",
        "Shift Name": "Evening",
        "Shift Start": "16:00",
        "Shift End": "22:00",
        "Service Charge": "0.00",
        "Transaction ID": "TX-12346",
        "Transaction Timestamp": "2026-06-20 18:30:00"
      }
    ]);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'POS_Import_Template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleUpload = async () => {
    if (!file) return;
    setStatus('');
    setImportResult(null);
    setIsUploading(true);
    try {
      const uploader = auth.currentUser?.email || auth.currentUser?.displayName || 'Unknown Uploader';
      
      const buffer = await file.arrayBuffer();
      const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

      const targetStoreObj = nonConsolidatedLocations.find(l => l.id === selectedTargetLocationId) || nonConsolidatedLocations[0];
      const targetLocId = targetStoreObj?.id;
      const targetLocName = targetStoreObj?.name;

      const result = await importPOSTips(
        tenantId, 
        file, 
        uploader, 
        hashHex, 
        isOverride, 
        isCorrection, 
        !!settings?.isPosTipAllocationRequired, 
        targetBatchId,
        targetLocId,
        targetLocName
      );
      setImportResult(result);
      await loadPending();
      
      if (result.orphans.length > 0) {
        setStatus(`Imported ${result.added} records to ${targetLocName || 'store'}. ${result.orphans.length} records require resolution.`);
      } else {
        setStatus(`Success! ${result.added} POS records imported for ${targetLocName || 'store'}.`);
        setFile(null);
      }
    } catch (error: any) {
      console.error('Import failed', error);
      
      let errorMessage = 'Error: Could not process file. Ensure column headers match the template.';
      
      const message = error instanceof Error ? error.message : String(error);
      if (message.includes('Duplicate')) {
        errorMessage = '⚠️ This file has already been imported. To re-import it, please check the "Mark as Authoritative Manager Override" box below and click Execute Sync again.';
      }
      
      setStatus(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  const handleStaffAdded = () => {
    setShowAddStaff(null);
    setStatus('New staff member added. Re-run import or map existing to resolve.');
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8 pb-20">
      {/* Tab Navigation */}
      <div className="flex p-1.5 bg-gray-100 rounded-2xl w-fit mx-auto mb-4 border border-gray-200">
        <button 
          onClick={() => handleTabChange('import')}
          className={`px-8 py-3 rounded-[1.25rem] text-[10px] font-black uppercase tracking-widest transition-all ${
            activeView === 'import' ? 'bg-white text-blue-600 shadow-sm border border-gray-200/50' : 'text-gray-400 hover:text-gray-600'
          }`}
        >
          New Import
        </button>
        <button 
          onClick={() => handleTabChange('history')}
          className={`px-8 py-3 rounded-[1.25rem] text-[10px] font-black uppercase tracking-widest transition-all relative ${
            activeView === 'history' ? 'bg-white text-blue-600 shadow-sm border border-gray-200/50' : 'text-gray-400 hover:text-gray-600'
          }`}
        >
          Import History
          {pendingImports.length > 0 && (
            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-amber-500 rounded-full border-2 border-gray-100 animate-pulse" />
          )}
        </button>
        <button 
          onClick={() => handleTabChange('config')}
          className={`px-8 py-3 rounded-[1.25rem] text-[10px] font-black uppercase tracking-widest transition-all ${
            activeView === 'config' ? 'bg-white text-blue-600 shadow-sm border border-gray-200/50' : 'text-gray-400 hover:text-gray-600'
          }`}
        >
          Config & Template
        </button>
      </div>

      {activeView === 'import' ? (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="card p-6 md:p-8 border-dashed border-2 relative overflow-hidden">

            <div className="space-y-8 relative z-10">
              {/* Target Store Destination Banner */}
              <div className="p-4 bg-slate-900 text-white rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-md">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-600/30 border border-blue-500/40 flex items-center justify-center text-blue-400 shrink-0">
                    <MapPin size={20} />
                  </div>
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">
                      Target Store Destination
                    </span>
                    <span className="text-sm font-black text-white">
                      {nonConsolidatedLocations.find(l => l.id === selectedTargetLocationId)?.name || activeLocation.name}
                    </span>
                  </div>
                </div>

                {nonConsolidatedLocations.length > 1 && (
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-slate-300 font-medium hidden sm:inline">Store Batch:</label>
                    <select
                      value={selectedTargetLocationId}
                      onChange={(e) => setSelectedTargetLocationId(e.target.value)}
                      className="bg-slate-800 border border-slate-700 text-white text-xs font-bold rounded-xl px-3 py-2 cursor-pointer focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    >
                      {nonConsolidatedLocations.map(loc => (
                        <option key={loc.id} value={loc.id}>
                          {loc.name} {loc.city ? `(${loc.city}, ${loc.state})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              <label className="group relative block transition-all">
                <input 
                  type="file" 
                  accept=".csv" 
                  onChange={handleFileChange} 
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" 
                />
                <div className={`border-2 border-dashed rounded-[2.5rem] p-16 text-center transition-all ${
                  file ? 'bg-blue-50 border-blue-400 ring-8 ring-blue-500/5' : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50/50'
                }`}>
                  {file ? (
                    <div className="flex flex-col items-center animate-in zoom-in-90 duration-300">
                      <div className="w-20 h-20 bg-blue-600 rounded-[2rem] flex items-center justify-center text-white mb-6 shadow-2xl shadow-blue-600/30">
                        <FileType size={40} />
                      </div>
                      <p className="text-xl font-black text-gray-900 tracking-tight italic">{file.name}</p>
                      <div className="flex items-center gap-3 mt-2">
                        <span className="text-[10px] font-black text-blue-500 uppercase tracking-widest">Payload Ready</span>
                        <div className="w-1 h-1 bg-blue-300 rounded-full" />
                        <span className="text-[10px] font-black text-blue-400 uppercase tracking-widest">{(file.size / 1024).toFixed(1)} KB</span>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center space-y-4">
                      <div className="w-20 h-20 bg-gray-50 rounded-[2rem] flex items-center justify-center text-gray-300 group-hover:text-blue-400 group-hover:bg-blue-50 transition-all duration-500">
                        <Upload size={40} />
                      </div>
                      <div>
                        <p className="text-lg font-black text-gray-900 tracking-tight uppercase italic">Select POS File</p>
                        <p className="text-xs text-gray-400 font-bold uppercase tracking-widest mt-1">Tap to browse or drop CSV payload</p>
                      </div>
                    </div>
                  )}
                </div>
              </label>

              <label className="flex items-center gap-3 p-4 bg-white rounded-2xl border border-gray-200 cursor-pointer hover:border-blue-300 transition-all">
                <input 
                  type="checkbox" 
                  checked={isOverride}
                  onChange={(e) => setIsOverride(e.target.checked)}
                  className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-xs font-black text-gray-700 uppercase tracking-widest">Mark as Authoritative Manager Override</span>
              </label>

              <label className="flex items-center gap-3 p-4 bg-white rounded-2xl border border-gray-200 cursor-pointer hover:border-blue-300 transition-all">
                <input 
                  type="checkbox" 
                  checked={isCorrection}
                  onChange={(e) => setIsCorrection(e.target.checked)}
                  className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-xs font-black text-gray-700 uppercase tracking-widest">This is a correction for a previous batch</span>
              </label>
              {(isCorrection || isOverride) && (
                  <select 
                      value={targetBatchId}
                      onChange={(e) => setTargetBatchId(e.target.value)}
                      className="w-full p-4 border rounded-2xl text-sm bg-white"
                  >
                      <option value="">Select batch to {isCorrection ? 'correct' : 'override'}...</option>
                      {history.map(h => <option key={h.id} value={h.batchId}>{h.filename} ({new Date(h.uploadedAt.toDate()).toLocaleDateString()}) - {h.batchId}</option>)}
                  </select>
              )}

              <button 
                onClick={handleUpload} 
                disabled={!file || isUploading}
                className="w-full h-20 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-100 disabled:text-gray-400 text-white rounded-[2rem] text-xl font-black uppercase italic tracking-widest flex items-center justify-center gap-4 shadow-2xl shadow-blue-600/30 transition-all active:scale-[0.98] tap-highlight-transparent"
              >
                {isUploading ? (
                  <>
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                    <span className="animate-pulse">Parsing Records...</span>
                  </>
                ) : (
                  <>
                    <Zap size={24} />
                    <span>Upload POS File</span>
                  </>
                )}
              </button>

              {status && (
                <div className={`p-6 rounded-[2rem] text-center text-[11px] font-black uppercase tracking-[0.1em] flex items-center justify-center gap-4 animate-in fade-in slide-in-from-top-4 shadow-sm border ${
                  status.includes('Success') 
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-100' 
                    : (status.includes('resolution')) ? 'bg-amber-50 text-amber-700 border-amber-100' : 'bg-rose-50 text-rose-700 border-rose-100'
                }`}>
                  {status.includes('Success') ? <CheckCircle size={18} className="shrink-0" /> : <AlertTriangle size={18} className="shrink-0" />}
                  <span className="leading-relaxed">{status}</span>
                </div>
              )}
            </div>
          </div>

          {importResult && importResult.orphans.length > 0 && (
            <div className="card p-10 bg-amber-50/30 border-amber-200 border-2 rounded-[3.5rem] animate-in slide-in-from-bottom-12 duration-700">
              <div className="flex items-center justify-between mb-10">
                <div className="flex items-center gap-4">
                  <div className="p-4 bg-amber-100 text-amber-600 rounded-[1.5rem] shadow-sm">
                    <AlertTriangle size={28} />
                  </div>
                  <div>
                    <h3 className="text-2xl font-black text-amber-900 uppercase italic tracking-tighter">Resolution Matrix</h3>
                    <p className="text-[11px] text-amber-600 font-bold uppercase tracking-[0.2em] mt-1">Target ID mismatch detected ({importResult.orphans.length} records)</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-[2.5rem] border border-amber-200 overflow-hidden shadow-2xl">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-amber-100/50">
                      <th className="px-8 py-5 text-[11px] font-black text-amber-900 uppercase tracking-widest">POS Origin ID</th>
                      <th className="px-8 py-5 text-[11px] font-black text-amber-900 uppercase tracking-widest">Metadata Name</th>
                      <th className="px-8 py-5 text-[11px] font-black text-amber-900 uppercase tracking-widest">Value Delta</th>
                      <th className="px-8 py-5 text-right text-[11px] font-black text-amber-900 uppercase tracking-widest">Resolution Path</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-amber-100">
                    {importResult.orphans.map((orphan, idx) => (
                      <tr key={idx} className="hover:bg-amber-50/50 transition-colors group">
                        <td className="px-8 py-5">
                            <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-lg font-mono text-xs font-black">{orphan.posId}</span>
                        </td>
                        <td className="px-8 py-5 text-sm font-bold text-gray-900">
                          {orphan.originalRow['Staff Name'] || orphan.originalRow['Employee'] || 'Unknown Anchor'}
                        </td>
                        <td className="px-8 py-5 text-lg font-black text-emerald-600 tracking-tighter">{formatCurrency(orphan.amount)}</td>
                        <td className="px-8 py-5 text-right">
                          <button 
                            onClick={() => {
                              const storeName = nonConsolidatedLocations.find(l => l.id === (orphan.targetLocationId || selectedTargetLocationId))?.name;
                              setShowAddStaff({
                                posId: orphan.posId,
                                locationId: orphan.targetLocationId || selectedTargetLocationId,
                                locationName: orphan.targetLocationName || storeName
                              });
                            }}
                            className="h-12 px-6 bg-amber-100 text-amber-900 rounded-[1rem] text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-2 hover:bg-amber-600 hover:text-white transition-all shadow-sm"
                          >
                            <UserPlus size={14} />
                            <span>Map Identity</span>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-8 p-6 bg-white border border-amber-200 rounded-[2rem] flex items-center gap-6">
                <div className="w-12 h-12 bg-amber-50 rounded-2xl flex items-center justify-center text-amber-600 shrink-0 border border-amber-100"><Info size={24} /></div>
                <p className="text-[11px] text-amber-800 leading-relaxed font-bold uppercase tracking-tight">
                  orphaned records are safely parked in the master ledger. By mapping these POS IDs to team members, you enable recursive reconciliation and release the funds for distribution.
                </p>
              </div>
            </div>
          )}
        </div>
      ) : activeView === 'history' ? (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
          {pendingImports.length > 0 ? (
            <div className="card p-10 bg-white border border-gray-100 rounded-[3rem] shadow-2xl shadow-gray-200/50">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-100 text-blue-600 rounded-2xl">
                    <History size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-black text-gray-900 uppercase italic tracking-tighter">Review Log</h3>
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-[0.2em] mt-1">Pending approval before distribution</p>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                {pendingImports.map((batch) => (
                  <div key={batch.date} className="group p-6 bg-gray-50/50 hover:bg-white hover:shadow-xl hover:border-gray-100 border border-transparent rounded-[2rem] transition-all flex items-center justify-between gap-6">
                    <div className="flex items-center gap-6">
                        <div className="w-14 h-14 bg-white rounded-2xl border border-gray-100 flex items-center justify-center shadow-sm">
                            <span className="text-[8px] font-black text-gray-400 uppercase leading-none mb-1">{batch.date.split('-')[1]}</span>
                            <span className="text-xl font-black text-gray-900 tracking-tighter leading-none">{batch.date.split('-')[2]}</span>
                        </div>
                        <div>
                            <p className="text-sm font-black text-gray-900 italic uppercase">{batch.date}</p>
                            <div className="flex items-center gap-3 mt-1">
                                <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest">{batch.count} Records</span>
                                <div className="w-1 h-1 bg-gray-300 rounded-full" />
                                <span className="text-[9px] font-black text-emerald-500 uppercase tracking-widest">Volume: {formatCurrency(batch.total)}</span>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <button 
                            onClick={() => handleDeleteImports(batch.ids, batch.date)}
                            disabled={isDeleting === batch.date}
                            className="p-4 text-rose-500 hover:text-white hover:bg-rose-500 bg-white border border-rose-100 rounded-2xl transition-all shadow-sm group-hover:shadow-rose-200"
                            title="Wipe Pending Batch"
                        >
                            {isDeleting === batch.date ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" /> : <Trash2 size={18} />}
                        </button>
                        <Link 
                            to={`/audit?date=${batch.date}`}
                            className="px-6 py-4 bg-white border border-gray-200 text-gray-900 rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-sm hover:border-blue-500 hover:text-blue-600 transition-all"
                        >
                            Review Details
                        </Link>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-8 p-6 bg-blue-50/50 border border-blue-100/50 rounded-2xl flex items-center gap-4">
                 <AlertTriangle size={20} className="text-blue-500 shrink-0" />
                 <p className="text-[11px] text-blue-700 leading-relaxed font-bold uppercase tracking-tight">
                   Records above are in a <strong>"Pending"</strong> state. They are visible in history but have not been locked into payroll. If you re-import for these dates, wipe the existing batch first to prevent duplicates.
                 </p>
              </div>
            </div>
          ) : (
            <div className="card p-20 text-center bg-gray-50 border-dashed border-2 border-gray-200 rounded-[3rem]">
               <div className="mx-auto w-20 h-20 bg-white rounded-3xl flex items-center justify-center text-gray-300 mb-6 shadow-sm">
                  <History size={40} />
               </div>
               <h3 className="text-xl font-black text-gray-900 uppercase italic tracking-tighter">No Active Ledger</h3>
               <p className="text-sm text-gray-400 font-bold uppercase tracking-widest mt-2 max-w-xs mx-auto">Import a file to populate the audit sandbox for reconciliation.</p>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
           <div className="card p-10 bg-white border border-gray-100 rounded-[3rem] shadow-2xl shadow-gray-200/50 space-y-8">
               <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-100 text-blue-600 rounded-2xl">
                    <Settings2 size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-black text-gray-900 uppercase italic tracking-tighter">Configuration & Template</h3>
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-[0.2em] mt-1">Configure bridge mapping and download the import template</p>
                  </div>
                </div>
                
                <button 
                  onClick={downloadTemplate}
                  className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-blue-600 hover:text-blue-700 bg-blue-50 px-6 py-4 rounded-xl border border-blue-100"
                >
                  <Download size={14} />
                  <span>Download POS Template</span>
                </button>
                
                <div className="p-8 bg-white border-2 border-blue-100 rounded-3xl animate-in zoom-in-95 duration-500">
                    <div className="flex items-center gap-4 mb-8">
                      <div className="p-3 bg-blue-100 text-blue-600 rounded-2xl">
                        <Settings2 size={24} />
                      </div>
                      <div>
                        <h3 className="text-lg font-black text-gray-900 uppercase italic tracking-tight">POS CSV Bridge Parameters</h3>
                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-[0.2em]">Map the columns from your POS CSV file for each item below</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                      <div className="space-y-6">
                        <h4 className="text-[10px] font-black text-blue-600 uppercase tracking-[0.3em]">Identity & Value</h4>
                        <div className="space-y-2">
                          <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest">POS ID Column (Enter Header/Column Name)</label>
                          <input 
                            type="text" 
                            value={mapping?.posId || ''} 
                            onChange={(e) => setMapping({ ...mapping, posId: e.target.value })}
                            placeholder="e.g., POS_ID"
                            className="w-full h-14 p-4 text-sm font-bold border-gray-200 bg-gray-50 rounded-2xl focus:ring-2 focus:ring-blue-500 transition-all outline-none"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Amount Column (Enter Header/Column Name)</label>
                          <input 
                            type="text" 
                            value={mapping?.amount || ''} 
                            onChange={(e) => setMapping({ ...mapping, amount: e.target.value })}
                            placeholder="e.g., Amount"
                            className="w-full h-14 p-4 text-sm font-bold border-gray-200 bg-gray-50 rounded-2xl focus:ring-2 focus:ring-blue-500 transition-all outline-none"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Date Column (Enter Header/Column Name)</label>
                          <input 
                            type="text" 
                            value={mapping?.tipDate || ''} 
                            onChange={(e) => setMapping({ ...mapping, tipDate: e.target.value })}
                            placeholder="e.g., Tip Date"
                            className="w-full h-14 p-4 text-sm font-bold border-gray-200 bg-gray-50 rounded-2xl focus:ring-2 focus:ring-blue-500 transition-all outline-none"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Service Charge / Auto-Grat Column (Optional)</label>
                          <input 
                            type="text" 
                            value={mapping?.serviceCharge || ''} 
                            onChange={(e) => setMapping({ ...mapping, serviceCharge: e.target.value })}
                            placeholder="e.g., Service Charge, Auto Gratuity"
                            className="w-full h-14 p-4 text-sm font-bold border-gray-200 bg-gray-50 rounded-2xl focus:ring-2 focus:ring-blue-500 transition-all outline-none"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Gross Sales / Receipts Column (Optional - for Form 8027)</label>
                          <input 
                            type="text" 
                            value={mapping?.grossSales || ''} 
                            onChange={(e) => setMapping({ ...mapping, grossSales: e.target.value })}
                            placeholder="e.g., Gross Sales, Total Sales (Optional)"
                            className="w-full h-14 p-4 text-sm font-bold border-gray-200 bg-gray-50 rounded-2xl focus:ring-2 focus:ring-blue-500 transition-all outline-none"
                          />
                        </div>
                      </div>

                      <div className="space-y-6">
                        <h4 className="text-[10px] font-black text-blue-600 uppercase tracking-[0.3em]">Temporal Delta</h4>
                        <div className="space-y-2">
                          <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Shift Name Column (Enter Header/Column Name)</label>
                          <input 
                            type="text" 
                            value={mapping?.shiftName || ''} 
                            onChange={(e) => setMapping({ ...mapping, shiftName: e.target.value })}
                            placeholder="e.g., Shift Name"
                            className="w-full h-14 p-4 text-sm font-bold border-gray-200 bg-gray-50 rounded-2xl focus:ring-2 focus:ring-blue-500 transition-all outline-none"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Start Time Column (Enter Header/Column Name)</label>
                          <input 
                            type="text" 
                            value={mapping?.startTime || ''} 
                            onChange={(e) => setMapping({ ...mapping, startTime: e.target.value })}
                            placeholder="e.g., Shift Start"
                            className="w-full h-14 p-4 text-sm font-bold border-gray-200 bg-gray-50 rounded-2xl focus:ring-2 focus:ring-blue-500 transition-all outline-none"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest">End Time Column (Enter Header/Column Name)</label>
                          <input 
                            type="text" 
                            value={mapping?.endTime || ''} 
                            onChange={(e) => setMapping({ ...mapping, endTime: e.target.value })}
                            placeholder="e.g., Shift End"
                            className="w-full h-14 p-4 text-sm font-bold border-gray-200 bg-gray-50 rounded-2xl focus:ring-2 focus:ring-blue-500 transition-all outline-none"
                          />
                        </div>
                      </div>
                    </div>

                    <button 
                      onClick={handleSaveMapping}
                      disabled={isSavingMapping}
                      className="w-full mt-10 h-16 bg-blue-600 text-white rounded-2xl text-xs font-black uppercase tracking-[0.2em] flex items-center justify-center gap-3 hover:bg-blue-700 transition-all shadow-xl shadow-blue-600/20 active:scale-[0.98]"
                    >
                      {isSavingMapping ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> : <Save size={18} />}
                      <span>Commit Bridge Logic</span>
                    </button>
                  </div>
           </div>
        </div>
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-950/80 backdrop-blur-xl animate-in fade-in duration-300">
           <div className="bg-white w-full max-w-sm rounded-[2rem] p-8 shadow-2xl animate-in zoom-in-95 duration-300 border border-white/20">
              <div className="w-16 h-16 bg-rose-50 rounded-2xl flex items-center justify-center text-rose-600 mb-6 mx-auto"><Trash2 size={32} /></div>
              <h3 className="text-lg font-black text-gray-900 uppercase italic tracking-tight text-center">Confirm Deletion</h3>
              <p className="text-xs text-gray-400 font-bold uppercase tracking-widest text-center mt-2 mb-8">This action cannot be undone. You are wiping {confirmDelete.ids.length} records for {confirmDelete.date}.</p>
              <div className="flex gap-3">
                 <button onClick={() => setConfirmDelete(null)} className="flex-1 h-14 bg-gray-100 text-gray-600 rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-gray-200 transition-all">Cancel</button>
                 <button onClick={executeDelete} className="flex-1 h-14 bg-rose-600 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-rose-700 transition-all">Yes, Wipe</button>
              </div>
           </div>
        </div>
      )}

      {showAddStaff && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-950/80 backdrop-blur-xl animate-in fade-in duration-500">
           <div className="bg-white w-full max-w-2xl rounded-[3rem] shadow-[0_40px_100px_rgba(0,0,0,0.5)] overflow-hidden border border-white/10 animate-in zoom-in-95 duration-500">
             <div className="p-8 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-blue-100 text-blue-600 rounded-[1.5rem] flex items-center justify-center shadow-sm"><UserPlus size={24} /></div>
                  <div>
                    <h3 className="text-xl font-black text-gray-900 uppercase italic tracking-tighter">Identity Resolution</h3>
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1">
                      Linking POS ID: <span className="text-blue-600 font-mono font-black">{showAddStaff.posId}</span>
                      {showAddStaff.locationName && (
                        <span className="ml-2 px-2 py-0.5 bg-blue-50 text-blue-700 rounded-md font-bold">
                          Store: {showAddStaff.locationName}
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                <button onClick={() => setShowAddStaff(null)} className="p-4 hover:bg-gray-200 rounded-3xl text-gray-400 transition-all"><X size={24} /></button>
             </div>
             <div className="p-10 max-h-[75vh] overflow-y-auto custom-scrollbar">
                <StaffManagementForm 
                  tenantId={tenantId} 
                  prefillPosId={showAddStaff.posId} 
                  defaultLocationId={showAddStaff.locationId}
                  onSuccess={handleStaffAdded}
                />
             </div>
           </div>
        </div>
      )}
    </div>
  );
};
